import React, { useState, useEffect } from "react";

import ClickTo from "web_modules/shared/CollapseExpandToggle";
import { Button, InlineNotification } from "carbon-components-react";
import {
  CloudDownload32,
  CloudUpload32,
  Delivery32,
  DocumentExport32,
  ShoppingCartArrowUp32,
  Upload32,
} from "@carbon/icons-react";
import APIClient from "web_modules/APIClient";

interface ReggoraInfo {
  loanAppID: number;
  respaDate: string;
  latestEventType: string | null;
  latestEventStatus: string | null;
}
const ReggoraWorkflowSection: React.FC<ReggoraInfo> = ({
  loanAppID,
  latestEventStatus,
  latestEventType,
  respaDate,
}: ReggoraInfo) => {
  const [reggoraLoanURL, setReggoraLoanURL] = useState<any>(null);
  const [reggoraOrderURL, setReggoraOrderURL] = useState<any>(null);
  const [isCollapsed, updateIsCollapsed] = useState(true);

  const fetchOrderInfoFromReggora = () => {
    APIClient.get(`/uw/${loanAppID}/reggora/order`, {})
      .then((resp: any) => {
        setReggoraLoanURL(resp.loanURL);
        setReggoraOrderURL(resp.orderURL);
      })
      .catch((err: any) => {
        if (err.meta && err.meta.error_detail) {
          alert(err.meta.error_detail);
        }
      });
  };

  const requestAppraisalFrom = (provider: string) => {
    APIClient.post(`/${loanAppID}/appraisal`, { provider })
      .then(() => {
        fetchOrderInfoFromReggora();
      })
      .catch((err: any) => {
        alert(err.meta && err.meta.error_detail);
      });
  };

  const createLoanInReggora = () => {
    APIClient.post(`/uw/${loanAppID}/reggora/loan`, {})
      .then(() => {
        fetchOrderInfoFromReggora();
      })
      .catch((err: any) => {
        if (err.meta && err.meta.error_detail) {
          alert(err.meta.error_detail);
        }
      });
  };
  const createOrderInReggora = () => {
    APIClient.post(`/uw/${loanAppID}/reggora/order`, {})
      .then(() => {
        fetchOrderInfoFromReggora();
      })
      .catch((err: any) => {
        if (err.meta && err.meta.error_detail) {
          alert(err.meta.error_detail);
        }
      });
  };

  const uploadDocsToReggoraOrder = () => {
    APIClient.post(`/uw/${loanAppID}/reggora/order/pc`, {})
      .then(() => {
        fetchOrderInfoFromReggora();
      })
      .catch((err: any) => {
        if (err.meta && err.meta.error_detail) {
          alert(err.meta.error_detail);
        }
      });
  };

  const updateLoanInReggora = () => {
    APIClient.put(`/uw/${loanAppID}/reggora/loan`, {})
      .then(() => {
        fetchOrderInfoFromReggora();
      })
      .catch((err: any) => {
        if (err.meta && err.meta.error_detail) {
          alert(err.meta.error_detail);
        }
      });
  };

  const forceUpdateAppraisalWithReggoraOrder = () => {
    APIClient.post(`/uw/${loanAppID}/reggora/order/update`, {})
      .then((resp: any) => {
        // todo would need to reload appraisal here...
      })
      .catch((err: any) => {
        if (err.meta && err.meta.error_detail) {
          alert(err.meta.error_detail);
        }
      });
  };

  useEffect(() => {
    fetchOrderInfoFromReggora();
  }, [loanAppID]);

  const buttons: Button[] = [
    <Button
      disabled={reggoraLoanURL !== "" || !respaDate}
      style={{ margin: "10px", width: "350px" }}
      onClick={() => {
        requestAppraisalFrom("reggora");
      }}
      renderIcon={ShoppingCartArrowUp32}
      title="Does the entire order flow: (1) Creates the loan file in reggora, (2) places an order, and (3) uploads the PC to the order."
    >
      Request Appraisal from Reggora
    </Button>,
    <Button
      style={{ margin: "10px", marginLeft: "30px", width: "300px" }}
      renderIcon={DocumentExport32}
      onClick={createLoanInReggora}
      disabled={reggoraLoanURL !== "" || !respaDate}
      title="Just step one of the order flow: (1) Creates the loan file in reggora."
    >
      1. Create Loan in Reggora
    </Button>,
    <Button
      style={{ margin: "10px", width: "300px" }}
      renderIcon={CloudUpload32}
      onClick={updateLoanInReggora}
      disabled={reggoraLoanURL === ""}
      title="Updates the loan in reggora, e.g. if the purchase price changes from the initial value at order time."
    >
      Update Loan in Reggora
    </Button>,
    <Button
      style={{ margin: "10px", marginLeft: "30px", width: "300px" }}
      renderIcon={Delivery32}
      onClick={createOrderInReggora}
      disabled={reggoraLoanURL === "" || reggoraOrderURL !== ""}
      title="Second step one of the order flow: (2) Creating an order for the loan file in reggora."
    >
      2. Place order through Reggora
    </Button>,
    <Button
      style={{ margin: "10px", width: "300px" }}
      renderIcon={CloudDownload32}
      onClick={forceUpdateAppraisalWithReggoraOrder}
      disabled={reggoraLoanURL === "" || reggoraOrderURL === ""}
      title="Fetches the order from reggora and updates values in our system if any dates have changed. Mostly an EPD debugging tool."
    >
      Retrieve dates from Reggora
    </Button>,
    <Button
      style={{ margin: "10px", marginLeft: "30px", width: "300px" }}
      renderIcon={Upload32}
      onClick={uploadDocsToReggoraOrder}
      disabled={reggoraLoanURL === "" || reggoraOrderURL === ""}
      title="Last step one of the order flow: (3) Uploading PC to the order. Also useful if there's an addendum we need to upload."
    >
      3. Upload PC to reggora order
    </Button>,
  ];

  const enabledButtons: Button[] = buttons
    .filter(b => !b.props.disabled)
    .map((b: Button) => {
      return {
        ...b,
        props: {
          ...b.props,
          style: { margin: "10px", width: "300px" },
        },
      };
    });

  return (
    <div>
      <div>
        {!respaDate && (
          <InlineNotification
            hideCloseButton
            kind="warning"
            notificationType="inline"
            role="alert"
            title="Cannot order appraisal until app is RESPA complete"
          />
        )}
        {respaDate && (
          <InlineNotification
            kind="info"
            notificationType="inline"
            role="alert"
            title={latestEventStatus && `Reggora - Latest status: ${latestEventStatus}`}
            subtitle={latestEventType && `(${latestEventType})`}
          />
        )}
        <ClickTo.Collapse
          isCollapsed={isCollapsed}
          collapseToggleFunc={() => updateIsCollapsed(!isCollapsed)}
        />
      </div>
      {isCollapsed &&
        enabledButtons.map(b => {
          return b;
        })}
      {!isCollapsed && (
        <>
          {buttons[0]}
          <br />
          {buttons[1]}
          {buttons[2]}
          <br />
          {buttons[3]}
          {buttons[4]}
          <br />
          {buttons[5]}
          <span style={{ position: "fixed", margin: "10px" }}>
            {reggoraLoanURL && (
              <p>
                <a href={reggoraLoanURL}>Open Reggora Loan File</a>
              </p>
            )}
            {reggoraOrderURL && (
              <p>
                <a href={reggoraOrderURL}>Open Reggora Order</a>
              </p>
            )}
          </span>
        </>
      )}
    </div>
  );
};

export default ReggoraWorkflowSection;
